import classNames from 'classnames'

import { UserReferralsRewardItem } from '@store'

import { ConditionStep } from '../../constants'
import { ConditionCard } from '../ConditionCard'
import { RewardCard, RewardCardState } from '../RewardCard'
import styles from './MilestoneItem.module.scss'

interface MilestoneItemProps {
  reward: UserReferralsRewardItem
  isFirst: boolean
  isLast: boolean
  isNextActive: boolean
  onClaim: (milestoneId: number) => void
  step: ConditionStep
}

function getConditionLabel(count: number): string {
  if (count <= 1) return 'Each New\nFriend'
  return `${count} Friends`
}

function getRewardState(reward: UserReferralsRewardItem, isFirst: boolean): RewardCardState {
  if ((reward.is_available && !reward.is_claimable) || (reward.is_claimable && reward.is_claimed)) return 'claimed'
  if (reward.is_available) return 'available'
  return 'locked'
}

function parseReward(
  reward: UserReferralsRewardItem,
): { amount: string; label: string; iconUrl?: string } {
  if (reward.reward_type === 'rating_score') {
    return {
      amount: String(reward.reward_meta_extra ?? '?'),
      label: 'Rating per friend',
    }
  }

  if (reward.reward_type === 'taps_limit') {
    const extra = Number(reward.reward_meta_extra ?? 0)

    return {
      amount: `+${extra}`,
      label: 'Taps Limit',
    }
  }

  if (reward.reward_type === 'item') {
    const itemId = reward.reward_meta[0]
    const itemDef = itemId ? reward.item_def_data?.[itemId] : undefined
    return {
      amount: '',
      label: itemDef?.name ?? 'Item',
      iconUrl: itemDef?.icon_url,
    }
  }

  const fallback = reward.reward_meta[0] ?? reward.reward_type
  return {
    amount: fallback,
    label: reward.reward_type,
  }
}

export const MilestoneItem = ({ reward, isFirst, isLast, isNextActive, onClaim, step }: MilestoneItemProps) => {
  const state = getRewardState(reward, isFirst)
  const conditionLabel = getConditionLabel(reward.referrals_count)
  const { amount, label, iconUrl } = parseReward(reward)
  const locked = state === 'locked'

  return (
    <div className={styles.row}>
      <ConditionCard
        count={reward.referrals_count}
        label={conditionLabel}
        locked={locked}
        step={step}
      />

      <div className={styles.connector}>
        {isFirst && <div className={styles.lineStartMarker} />}
        <div className={classNames(styles.line, styles.lineTop, {
          [styles.lineTopFirst]: isFirst,
          [styles.lineActive]: state !== 'locked',
        })} />
        <div className={classNames(styles.ball, { [styles.ballLocked]: locked })}>
          <div className={styles.ballOuter} />
          <div className={styles.ballInner} />
        </div>
        <div className={classNames(styles.line, styles.lineBottom, {
          [styles.hidden]: isLast,
          [styles.lineActive]: isNextActive,
        })} />
      </div>

      <RewardCard
        amount={amount}
        label={label}
        state={state}
        iconUrl={iconUrl}
        onClaim={() => onClaim(reward.milestone_id)}
      />
    </div>
  )
}
